import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { FONT_MAP } from '../../../utils/commonRingUtils';
import { IOption, ISwatchContent } from '../../types/customizer';
import If from '../If';
import SwatchOption from '../SwatchOption';

export interface IEngravingFilterProps {
  content: ISwatchContent;
  onChange: (updated: any) => void;
  innerTab?: number;
  isFetching: boolean;
}

const EngravingFilter = ({ content, onChange, isFetching, innerTab = 1 }: IEngravingFilterProps) => {

  const options: Array<IOption> = content.options as Array<IOption>;
  const { hasSwatch, fieldType, selectedOption, engravingText } = content;
  const [isChanging, setIsChanging] = useState('');
  const [font, setFont] = useState<IOption>();
  const [text, setText] = useState('');

  useEffect(() => {
    if (!isFetching) {
      setIsChanging('');
    }
  }, [isFetching]);

  useEffect(() => {
  /**
   * For Engraving tab, set selectedOption and text in state to manage local state change
   */
    setText(engravingText || '');
    setFont(selectedOption);
  }, [engravingText, selectedOption]);

  const handleFontChange = (updatedObj: Record<string, string>) => {
    const sku = Object.values(updatedObj)[0];
    const selectedFont = options.find(item => item.type === 'option' && item.sku === sku);
    if (selectedFont?.type === 'option') {
      if (!font || selectedFont?.sku !== font.sku) {
        setFont(selectedFont);
      }
      handleUpdate(selectedFont, selectedFont.sku === 'NONE' ? '' : text);
    }
  };

  const handleTextChange = (e: any) => {
    if (isFetching) {
      return;
    }
    const { value } = e.target;
    if (value.length <= 35) {
      setText(value);
    }
  };

  const handleFontNameChange = (e: any) => {
    const { value } = e.target;
    if (font) {
      const updatedFont = {
        ...font,
        name: value,
      };
      setFont(updatedFont);
    }
  };

  const handleBlur = () => {
    if(engravingText !== text) {
      handleUpdate(font, text);
    }
  }

  const handleFontNameBlur = () => {
    if(selectedOption?.name !== font?.name) {
      handleUpdate(font, text);
    }
  }

  const handleUpdate = (font: IOption | undefined, text: string) => {
    const engraving_new = `I_${font?.sku}-${font?.name}_${text}`;
    /**
     * Check font name is availble to check custom font is availble
     * then check if none then allow to update
     * Else text is available then allows to update
     */
    if (font?.name && (font?.sku === 'NONE' || text)) {
      onChange({ engraving_new });
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-justify-center tw-pb-2">
      <div className="tw-w-full tw-h-full sm:tw-w-full tw-px-4">
        <div className="tw-overflow-y-auto tw-overflow-x-hidden tw-h-full tw-pb-2">
          <div className="tw-mt-3 tw-px-1.5 sm:tw-px-2 tw-text-lg tw-leading-snug primary-font-family tw-text-primary-color">Choose Your Font</div>
          <If condition={!font || font.sku === 'NONE'}>
            <div className="tw-mt-0.5 tw-mb-2 tw-px-1.5 sm:tw-px-2 tw-text-sm tw-text-secondary-color">Select a font to add your engraving.</div>
          </If>
          <div className="tw-flex tw-flex-wrap tw-overflow-auto">
            {options.map((option: IOption, index: number) => (
              <SwatchOption
                key={index}
                {...option}
                selected={option.sku === font?.sku}
                hasSwatch={hasSwatch}
                fieldType={fieldType}
                onChange={handleFontChange}
                selectedOption={selectedOption}
                isChanging={isChanging}
              />
            ))}
          </div>
          <If condition={font && font.sku !== 'NONE'}>
            <If condition={font?.sku === 'CUSTOM'}>
              <div className="tw-mt-5 tw-px-1.5 sm:tw-px-2 tw-max-w-2xl tw-box-content">
                <label htmlFor="engraving-font-name" className="tw-block tw-mb-2 tw-text-lg primary-font-family tw-text-primary-color">Font Name</label>
                <div>
                  <input
                    id="engraving-font-name"
                    type="text"
                    value={font?.name || ''}
                    onChange={handleFontNameChange}
                    onBlur={handleFontNameBlur}
                    placeholder="(none)"
                    className={clsx({
                      'tw-px-3 tw-py-2 tw-w-full tw-text-lg tw-text-primary-color tw-bg-secondary-bg-color tw-border tw-border-solid tw-border-secondary-border-color tw-rounded tw-outline-none focus:tw-border-brand-color': true,
                      'tw-border-red': font?.name.length === 0
                    })}
                  />
                </div>
              </div>
            </If>
            <div className="tw-mt-5 tw-px-1.5 sm:tw-px-2 tw-max-w-2xl tw-box-content tw-pb-20">
              <label htmlFor="engraving-text" className="tw-block tw-mb-2 tw-text-lg primary-font-family tw-text-primary-color">Enter Your Engraving</label>
              <div>
                <input
                  id="engraving-text"
                  type="text"
                  value={text}
                  onChange={handleTextChange}
                  onBlur={handleBlur}
                  placeholder="(none)"
                  className={clsx({
                    'tw-px-3 tw-py-2 tw-w-full tw-text-lg tw-text-primary-color tw-bg-secondary-bg-color tw-border tw-border-solid tw-border-secondary-border-color tw-rounded tw-outline-none focus:tw-border-brand-color': true,
                    'tw-border-red': text.length === 0
                  })}
                />
                <If condition={!!text && font?.sku !== 'CUSTOM'}>
                  <div className="tw-mt-3 tw-p-4 tw-bg-primary-bg-color tw-border tw-border-solid tw-border-secondary-border-color tw-rounded">
                    <div className="tw-text-xs tw-text-secondary-color tw-mb-2">Preview</div>
                    <div
                      className={clsx(FONT_MAP[font?.name || ''], 'tw-leading-normal tw-text-primary-color tw-break-words engraving-font-size engraving-font-size-preview')}
                    >
                      {text}
                    </div>
                  </div>
                </If>
                <div className="tw-text-sm tw-text-secondary-color tw-font-normal tw-mt-2">{(35 - text.length)} characters left</div>
                <div className="tw-text-sm tw-text-secondary-color tw-font-normal tw-mt-2">*Note: Your engraving text will not appear on the ring image.</div>
              </div>
            </div>
          </If>
        </div>
      </div>
    </div>
  );
};

export default EngravingFilter;

